import { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FiHome, FiGrid, FiUsers, FiLogOut, FiTag,
  FiSearch, FiLayers, FiUser, FiSettings, FiShield,
  FiCalendar, FiInbox, FiTrendingUp, FiFileText, FiArchive,
  FiHeart, FiBookOpen, FiChevronLeft, FiChevronRight,
  FiEdit2, FiBarChart2, FiHelpCircle, FiSliders, FiRepeat, FiBell, FiCreditCard,
  FiUserCheck, FiKey,
} from 'react-icons/fi';
import { useNotifications } from '../../context/NotificationContext';
import styles from './Sidebar.module.css';

// Maps each group key → route prefixes that belong to it
const SECTION_ROUTES = {
  // Listed first so its specific /management/* and /super-admin/* routes take
  // precedence over the broader 'administration' and 'super-admin' prefixes.
  'preferences-tools': [
    '/notification-preferences',
    '/management/notification-settings',
    '/management/co-admins',
    '/management/online-payments',
    '/super-admin/notification-channels',
    '/super-admin/notification-usage',
    '/utils', '/preferences', '/analytics', '/my-tools',
  ],
  derasar:        ['/derasar', '/events', '/notices'],
  'my-account':   ['/family', '/my-persons', '/address-book', '/member-reports'],
  administration: ['/management', '/users', '/derasar/families'],
  accounting:     ['/accounting/financial-years', '/accounting/chart-of-accounts', '/accounting/ledger', '/accounting/reports'],
  transactions:   ['/accounting/offerings', '/accounting/receipts', '/accounting/payments', '/accounting/journal-entries', '/pay-offers'],
  // 'support' before 'super-admin' so /super-admin/support expands Support, not Super Admin.
  support:        ['/help', '/super-admin/support'],
  'super-admin':  ['/super-admin'],
};

const ALL_KEYS = Object.keys(SECTION_ROUTES);

const STORAGE_KEY = 'sidebar_groups';

const getActiveKey = (pathname) => {
  for (const [key, prefixes] of Object.entries(SECTION_ROUTES)) {
    if (prefixes.some((p) => pathname.startsWith(p))) return key;
  }
  return null;
};

const loadPersistedKeys = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* ignore */ }
  return new Set();
};

const persistKeys = (set) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
};

// ── NavItem ──────────────────────────────────────────────────────────────────
const NavItem = ({ to, icon: Icon, label, onClick, end, badge }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    data-tooltip={label}
    className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
  >
    <Icon className={styles.navIcon} />
    <span className={styles.navLabel}>{label}</span>
    {badge > 0 && <span className={styles.navBadge}>{badge > 99 ? '99+' : badge}</span>}
  </NavLink>
);

// ── NavGroup ─────────────────────────────────────────────────────────────────
const NavGroup = ({ id, label, children, sidebarCollapsed, expanded, onToggle, totalBadge }) => {
  // In icon-only mode: show all items without the collapsible header
  if (sidebarCollapsed) {
    return <div className={styles.navSection}>{children}</div>;
  }

  return (
    <div className={styles.navSection}>
      <button
        className={`${styles.navGroupHeader} ${expanded ? styles.navGroupOpen : ''}`}
        onClick={() => onToggle(id)}
        aria-expanded={expanded}
      >
        <span className={styles.navGroupHeaderLeft}>{label}</span>
        <span className={styles.navGroupHeaderLeft}>
          {!expanded && totalBadge > 0 && (
            <span className={styles.navGroupBadge}>{totalBadge > 99 ? '99+' : totalBadge}</span>
          )}
          <FiChevronRight
            className={`${styles.navGroupChevron} ${expanded ? styles.navGroupChevronOpen : ''}`}
            size={10}
          />
        </span>
      </button>
      {expanded && <div className={styles.navGroupItems}>{children}</div>}
    </div>
  );
};

// ── Sidebar ───────────────────────────────────────────────────────────────────
const Sidebar = ({ open, onClose, collapsed, onToggleCollapse }) => {
  const { user, logout, activeDerasarId, isDerasarAdmin, isDerasarOwner, can, activeDerasar } = useAuth();
  const { notifications } = useNotifications();
  const location = useLocation();

  const hasActiveDerasar = !!activeDerasarId;

  const countUnread = (...types) =>
    notifications.filter(n => !n.isRead && types.includes(n.type)).length;

  const offeringsBadge     = countUnread('offering_created', 'offer_status_changed');
  const receiptsBadge      = countUnread('offering_payment_received');
  const myDerasarBadge     = countUnread('member_join_approved', 'member_join_rejected');
  const membersBadge       = countUnread('member_join_request');
  const familyBadge        = countUnread('family_join_request', 'family_approved', 'family_rejected', 'member_added_to_family');
  const financialYearBadge = countUnread('financial_year_closed');
  const demoRequestsBadge  = countUnread('demo_request_submitted');

  // Expanded sections state — default all collapsed, restore from localStorage
  const [expandedSections, setExpandedSections] = useState(() => {
    const persisted = loadPersistedKeys();
    const activeKey = getActiveKey(location.pathname);
    if (activeKey) persisted.add(activeKey);
    return persisted;
  });

  // Auto-expand the active section whenever the route changes
  useEffect(() => {
    const activeKey = getActiveKey(location.pathname);
    if (!activeKey) return;
    setExpandedSections((prev) => {
      if (prev.has(activeKey)) return prev;
      const next = new Set(prev);
      next.add(activeKey);
      persistKeys(next);
      return next;
    });
  }, [location.pathname]);

  const toggle = useCallback((id) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistKeys(next);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const next = new Set(ALL_KEYS);
    persistKeys(next);
    setExpandedSections(next);
  }, []);

  const collapseAll = useCallback(() => {
    // Keep active section open
    const activeKey = getActiveKey(location.pathname);
    const next = activeKey ? new Set([activeKey]) : new Set();
    persistKeys(next);
    setExpandedSections(next);
  }, [location.pathname]);

  const isExpanded = (id) => expandedSections.has(id);

  return (
    <>
      <div className={`${styles.overlay} ${open ? styles.show : ''}`} onClick={onClose} />
      <aside className={`${styles.sidebar} ${open ? styles.open : ''} ${collapsed ? styles.collapsed : ''}`}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.logo}>
            <img src="/icon.png" alt="Jainam Seva Setu" className={styles.logoImage} />
            <span className={styles.appName}>
              <span className={styles.brandBlue}>Jainam</span>
              <span className={styles.brandGold}>Seva</span>
              <span className={styles.brandBlue}>Setu</span>
            </span>
          </div>
          <button className={styles.collapseBtn} onClick={onToggleCollapse} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
        </div>

        {/* Expand / Collapse All */}
        {!collapsed && (
          <div className={styles.expandCollapseBar}>
            <button className={styles.expandCollapseBtn} onClick={expandAll}>Expand All</button>
            <button className={styles.expandCollapseBtn} onClick={collapseAll}>Collapse All</button>
          </div>
        )}

        {/* Navigation */}
        <nav className={styles.nav}>

          {/* Dashboard — no group */}
          <div className={styles.navSection}>
            <NavItem to="/dashboard" icon={FiHome} label="Dashboard" onClick={onClose} />
          </div>

          {/* Derasar */}
          <NavGroup
            id="derasar"
            label="Derasar"
            sidebarCollapsed={collapsed}
            expanded={isExpanded('derasar')}
            onToggle={toggle}
            totalBadge={myDerasarBadge}
          >
            {user?.role !== 'derasar_admin' && (
              <NavItem to="/derasar/browse" icon={FiSearch} label="Browse Derasars" onClick={onClose} />
            )}
            {hasActiveDerasar && (
              <NavItem to="/derasar/my" icon={FiGrid} label="My Derasar" onClick={onClose} badge={myDerasarBadge} />
            )}
            {hasActiveDerasar && (!isDerasarAdmin || can('eventsNotices', 'view')) && (
              <NavItem to="/derasar/events" icon={FiBookOpen} label="Events" onClick={onClose} />
            )}
            {hasActiveDerasar && (!isDerasarAdmin || can('eventsNotices', 'view')) && (
              <NavItem to="/derasar/notices" icon={FiFileText} label="Notice Board" onClick={onClose} />
            )}
          </NavGroup>

          {/* My Account — regular users only */}
          {user.role === 'user' && hasActiveDerasar && (
            <NavGroup
              id="my-account"
              label="My Account"
              sidebarCollapsed={collapsed}
              expanded={isExpanded('my-account')}
              onToggle={toggle}
              totalBadge={familyBadge}
            >
              <NavItem to="/family" icon={FiUsers} label="My Family" onClick={onClose} badge={familyBadge} />
              <NavItem to="/my-persons" icon={FiHeart} label="My Persons" onClick={onClose} />
              <NavItem to="/address-book" icon={FiBookOpen} label="Address Book" onClick={onClose} />
              <NavItem to="/member-reports" icon={FiBarChart2} label="My Reports" onClick={onClose} />
            </NavGroup>
          )}

          {/* Administration */}
          {((can('members', 'view') || can('donationCategories', 'view')) || user?.role === 'super_admin' || user?.role === 'platform_admin') && hasActiveDerasar && (
            <NavGroup
              id="administration"
              label="Administration"
              sidebarCollapsed={collapsed}
              expanded={isExpanded('administration')}
              onToggle={toggle}
              totalBadge={membersBadge}
            >
              {(can('donationCategories', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/management/categories" icon={FiTag} label="Donation Categories" onClick={onClose} />
              )}
              {(can('members', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/users" icon={FiUsers} label="Members" onClick={onClose} badge={membersBadge} />
              )}
              {(can('members', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/derasar/families" icon={FiUsers} label="Families" onClick={onClose} />
              )}
              {(can('members', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/address-book" icon={FiBookOpen} label="Address Book" onClick={onClose} />
              )}
            </NavGroup>
          )}

          {/* Accounting */}
          {((can('accountingSetup', 'view') || can('reports', 'view')) || user?.role === 'super_admin' || user?.role === 'platform_admin') && hasActiveDerasar && (
            <NavGroup
              id="accounting"
              label="Accounting"
              sidebarCollapsed={collapsed}
              expanded={isExpanded('accounting')}
              onToggle={toggle}
              totalBadge={financialYearBadge}
            >
              {(can('accountingSetup', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/accounting/financial-years" icon={FiCalendar} label="Financial Years" onClick={onClose} badge={financialYearBadge} />
              )}
              {(can('accountingSetup', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/accounting/chart-of-accounts" icon={FiLayers} label="Chart of Accounts" onClick={onClose} />
              )}
              {(can('accountingSetup', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/accounting/ledger" icon={FiInbox} label="Ledger" onClick={onClose} />
              )}
              {(can('reports', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/accounting/reports" icon={FiTrendingUp} label="Reports" onClick={onClose} />
              )}
              {(can('reports', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/member-reports" icon={FiBarChart2} label="Member Reports" onClick={onClose} />
              )}
            </NavGroup>
          )}

          {/* Transactions */}
          {hasActiveDerasar && (!isDerasarAdmin || can('offerings', 'view') || can('receipts', 'view') || can('payments', 'view') || can('journalEntries', 'view')) && (
            <NavGroup
              id="transactions"
              label="Transactions"
              sidebarCollapsed={collapsed}
              expanded={isExpanded('transactions')}
              onToggle={toggle}
              totalBadge={offeringsBadge + receiptsBadge}
            >
              {(!isDerasarAdmin || can('offerings', 'view')) && (
                <NavItem to="/accounting/offerings" icon={FiArchive} label="Offerings" onClick={onClose} badge={offeringsBadge} />
              )}
              {(!isDerasarAdmin || can('receipts', 'view')) && (
                <NavItem to="/accounting/receipts" icon={FiInbox} label="Receipts" onClick={onClose} badge={receiptsBadge} />
              )}
              {(!isDerasarAdmin && user?.role === 'user' && activeDerasar?.derasarId?.onlinePaymentEnabled) && (
                <NavItem to="/pay-offers" icon={FiTrendingUp} label="Pay Offers" onClick={onClose} />
              )}
              {(can('payments', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/accounting/payments" icon={FiFileText} label="Payments" onClick={onClose} />
              )}
              {(can('journalEntries', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/accounting/journal-entries" icon={FiEdit2} label="Journal Entries" onClick={onClose} />
              )}
            </NavGroup>
          )}

        {/* Super Admin */}
          {(user.role === 'super_admin' || user.role === 'platform_admin') && (
            <NavGroup
              id="super-admin"
              label="Super Admin"
              sidebarCollapsed={collapsed}
              expanded={isExpanded('super-admin')}
              onToggle={toggle}
              totalBadge={demoRequestsBadge}
            >
              <NavItem to="/super-admin" icon={FiGrid} label="Derasar Approvals" onClick={onClose} end />
              <NavItem to="/super-admin/demo-requests" icon={FiUserCheck} label="Demo Requests" onClick={onClose} badge={demoRequestsBadge} />
              <NavItem to="/super-admin/trial-accounts" icon={FiKey} label="Trial Accounts" onClick={onClose} />
              <NavItem to="/super-admin/online-payments" icon={FiCreditCard} label="Online Payments" onClick={onClose} />
              {user?.isSuperAdmin && (
                <NavItem to="/super-admin/settings" icon={FiSettings} label="Subscription Settings" onClick={onClose} />
              )}
              {user?.isSuperAdmin && (
                <NavItem to="/super-admin/admins" icon={FiShield} label="Platform Admins" onClick={onClose} />
              )}
            </NavGroup>
          )}
          
          {/* Preferences & Tools — notification settings, co-admins, analytics, tools */}
          {((user.role === 'user' && hasActiveDerasar) || isDerasarOwner || can('utils', 'view') || can('utils', 'manage') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
            <NavGroup
              id="preferences-tools"
              label="Preferences & Tools"
              sidebarCollapsed={collapsed}
              expanded={isExpanded('preferences-tools')}
              onToggle={toggle}
              totalBadge={0}
            >
              {/* Notifications */}
              {user.role === 'user' && hasActiveDerasar && (
                <NavItem to="/notification-preferences" icon={FiBell} label="Notification Settings" onClick={onClose} />
              )}
              {can('utils', 'manage') && (
                <NavItem to="/management/notification-settings" icon={FiBell} label="Notification Settings" onClick={onClose} />
              )}
              {(user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <>
                  <NavItem to="/super-admin/notification-channels" icon={FiSliders} label="Notification Settings" onClick={onClose} />
                  <NavItem to="/super-admin/notification-usage" icon={FiBell} label="Notification Usage" onClick={onClose} />
                </>
              )}
              {/* Co-Admins (owner only) */}
              {isDerasarOwner && (
                <NavItem to="/management/co-admins" icon={FiShield} label="Co-Admins" onClick={onClose} />
              )}
              {/* Online Payments — when enabled for this Derasar */}
              {can('utils', 'manage') && activeDerasar?.derasarId?.onlinePaymentEnabled && (
                <NavItem to="/management/online-payments" icon={FiCreditCard} label="Online Payments" onClick={onClose} />
              )}
              {/* Tools */}
              {(can('utils', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && hasActiveDerasar && (
                <>
                  <NavItem to="/utils/pdf-config" icon={FiFileText} label="PDF Configuration" onClick={onClose} />
                  <NavItem to="/preferences" icon={FiSettings} label="User Preferences" onClick={onClose} />
                </>
              )}
              {/* Analytics */}
              {(can('utils', 'view') || user?.role === 'super_admin' || user?.role === 'platform_admin') && (
                <NavItem to="/analytics" icon={FiBarChart2} label="Analytics" onClick={onClose} />
              )}
              {/* Persons Migration — regular members only */}
              {!isDerasarAdmin && user?.role !== 'super_admin' && user?.role !== 'platform_admin' && hasActiveDerasar && (
                <NavItem to="/my-tools/persons-migration" icon={FiRepeat} label="Persons Migration" onClick={onClose} />
              )}
            </NavGroup>
          )}
          
          {/* Support */}
          <NavGroup
            id="support"
            label="Support"
            sidebarCollapsed={collapsed}
            expanded={isExpanded('support')}
            onToggle={toggle}
            totalBadge={0}
          >
            <NavItem to="/help" icon={FiHelpCircle} label="Help & Support" onClick={onClose} />
            {(user.role === 'super_admin' || user.role === 'platform_admin') && (
              <NavItem to="/super-admin/support" icon={FiInbox} label="Support Tickets" onClick={onClose} />
            )}
          </NavGroup>
        </nav>

        {/* Footer */}
        <div className={styles.footer}>
          <NavItem to="/profile" icon={FiUser} label="My Profile" onClick={onClose} />
          <button className={styles.navBtn} data-tooltip="Logout" onClick={() => { logout(); onClose(); }}>
            <FiLogOut className={styles.navIcon} />
            <span className={styles.navLabel}>Logout</span>
          </button>
          {!collapsed && (
            <div className={styles.brandingFooter}>
              <span className={styles.brandingText}>JainamSevaSetu</span>
              <span className={styles.brandingCopy}>&copy; {new Date().getFullYear()} All rights reserved</span>
            </div>
          )}
        </div>

      </aside>
    </>
  );
};

export default Sidebar;
